import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Package, Star, Clock, Shield, MessageSquare } from 'lucide-react';
import { api } from '../context/AuthContext';

export default function ShopProfile() {
  const { id } = useParams();
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;
    
    const fetchShopData = async () => {
      setLoading(true);
      try {
        // Fetch shop public info
        const shopRes = await api.get(`/public/shops/${id}`);
        if (!isMounted) return;
        if (!shopRes.data) {
          setError('Negocio no encontrado');
          return;
        }
        setShop(shopRes.data);
        
        // Fetch products by this seller
        try {
          const productsRes = await api.get(`/products?seller_id=${id}&status=ACTIVE`);
          if (isMounted) {
            setProducts(productsRes.data?.products || []);
          }
        } catch (prodErr) {
          console.error('Error fetching products:', prodErr);
          if (isMounted) setProducts([]);
        }
      } catch (err) {
        console.error('Error fetching shop:', err);
        if (isMounted) {
          const errorMsg = err.response?.data?.error || err.message || 'Error al cargar información del negocio';
          setError(errorMsg);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    
    fetchShopData();
    
    return () => {
      isMounted = false;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !shop) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🔍</div>
          <h2 className="text-xl font-bold">{error || 'Negocio no encontrado'}</h2>
          <Link to="/talleres" className="btn-primary mt-4 inline-block">
            Ver mapa de servicios
          </Link>
        </div>
      </div>
    );
  }

  const shopTypeLabels = {
    TALLER: { name: 'Taller', icon: '🔧', color: 'bg-red-500' },
    REPUESTERA: { name: 'Repuestera', icon: '🛞', color: 'bg-blue-500' },
    GRUA: { name: 'Servicio de Grúa', icon: '🚛', color: 'bg-orange-500' }
  };

  const shopInfo = shopTypeLabels[shop.shop_type] || { name: 'Negocio', icon: '🏪', color: 'bg-gray-500' };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Link to="/talleres" className="text-gray-300 hover:text-white mb-4 inline-flex items-center gap-1 text-sm">
            ← Volver al mapa
          </Link>
          
          <div className="flex items-start gap-6">
            {/* Shop avatar */}
            <div className={`w-24 h-24 rounded-2xl ${shopInfo.color} flex items-center justify-center text-4xl shadow-lg`}>
              {shopInfo.icon}
            </div>
            
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold">{shop.name}</h1>
                <span className={`${shopInfo.color} text-white text-xs px-3 py-1 rounded-full font-medium`}>
                  {shopInfo.icon} {shopInfo.name}
                </span>
              </div>
              
              {/* Location info */}
              {shop.shop_address && (
                <div className="flex items-center gap-2 text-gray-300 mb-2">
                  <MapPin className="w-4 h-4" />
                  <span>{shop.shop_address}</span>
                </div>
              )}
              
              {/* Province */}
              {shop.province && (
                <p className="text-gray-400 text-sm">{shop.province.replace('_', ' ')}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Stats cards */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl p-6 text-center shadow-sm">
            <Package className="w-8 h-8 text-primary mx-auto mb-2" />
            <p className="text-2xl font-bold">{products.length}</p>
            <p className="text-sm text-gray-500">Productos</p>
          </div>
          <div className="bg-white rounded-xl p-6 text-center shadow-sm">
            <Star className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
            <p className="text-2xl font-bold">{shop.is_verified ? '4.8' : '-'}</p>
            <p className="text-sm text-gray-500">Calificación</p>
          </div>
          <div className="bg-white rounded-xl p-6 text-center shadow-sm">
            <Shield className="w-8 h-8 text-green-500 mx-auto mb-2" />
            <p className={`text-lg font-bold ${shop.is_verified ? 'text-green-600' : 'text-gray-400'}`}>
              {shop.is_verified ? 'Verificado ✓' : 'Sin verificar'}
            </p>
          </div>
        </div>

        {/* Contact info */}
        <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Phone className="w-5 h-5 text-primary" />
            Información de Contacto
          </h2>
          <div className="space-y-3">
            {shop.phone && (
              <a href={`tel:${shop.phone}`} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                <Phone className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-sm text-gray-500">Teléfono</p>
                  <p className="font-semibold">{shop.phone}</p>
                </div>
              </a>
            )}
            <a href={`mailto:${shop.email}`} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
              <Mail className="w-5 h-5 text-primary" />
              <div>
                <p className="text-sm text-gray-500">Correo electrónico</p>
                <p className="font-semibold">{shop.email}</p>
              </div>
            </a>
          </div>
        </div>

        {/* Contact CTA */}
        <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
          <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-primary" />
            Contactar
          </h2>
          <p className="text-gray-600 mb-4">
            ¿Necesitas ayuda o tienes preguntas? Contacta directamente con este negocio.
          </p>
          <Link 
            to={`/messages/${shop.id}`}
            className="btn-primary inline-flex items-center gap-2"
          >
            <MessageSquare className="w-4 h-4" />
            Enviar mensaje
          </Link>
        </div>

        {/* Location */}
        {shop.shop_address && (
          <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
            <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" />
              Ubicación
            </h2>
            <p className="text-gray-600">{shop.shop_address}</p>
            {shop.shop_lat && shop.shop_lng && (
              <a 
                href={`https://www.google.com/maps?q=${shop.shop_lat},${shop.shop_lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-3 text-primary font-medium hover:underline"
              >
                📍 Ver en Google Maps →
              </a>
            )}
          </div>
        )}

        {/* Products section */}
        {products.length > 0 && (
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <Package className="w-5 h-5 text-primary" />
              Productos ({products.length})
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {products.slice(0, 12).map(product => (
                <Link 
                  key={product.id} 
                  to={`/products/${product.id}`}
                  className="bg-gray-50 rounded-lg overflow-hidden hover:shadow-md transition group"
                >
                  <div className="aspect-square bg-gray-100 overflow-hidden">
                    {product.images?.[0]?.url ? (
                      <img 
                        src={product.images[0].url} 
                        alt={product.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-3xl">
                        {product.is_accessory ? '🧤' : product.product_type === 'consumable' ? '🛢️' : '🛞'}
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <h3 className="text-sm font-medium line-clamp-1">{product.title}</h3>
                    <p className="font-bold text-primary">₡{Number(product.price).toLocaleString()}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      product.condition === 'NEW' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {product.condition === 'NEW' ? 'Nuevo' : 'Usado'}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
            
            {products.length > 12 && (
              <p className="text-center text-gray-500 mt-4">
                y {products.length - 12} productos más...
              </p>
            )}
          </div>
        )}

        {products.length === 0 && (
          <div className="bg-white rounded-xl p-8 text-center shadow-sm">
            <div className="text-5xl mb-4">📦</div>
            <h3 className="text-lg font-semibold">Sin productos publicados</h3>
            <p className="text-gray-500 mt-2">Este negocio aún no tiene productos en venta.</p>
          </div>
        )}
      </div>
    </div>
  );
}